/**
 * Question Set Schema — MCQ, Match, FITB (dropdown / type)
 */

import type {
    CompositeQuestionsLayoutFields,
    ContentConfig,
    LayoutConfig,
    ScoringConfig,
} from '@ai-content-hub/content-core';
import type { MCQElementData } from '@ai-content-hub/content-elements';
import type { MatchElementData } from '@ai-content-hub/content-elements';
import type { FITBElementData } from '@ai-content-hub/content-elements';
import type { OrderingElementData } from '@ai-content-hub/content-elements';
import type { PageElementData } from '@ai-content-hub/content-elements';
import { defaultPageData } from '@ai-content-hub/content-elements';

export type { PageElementData } from '@ai-content-hub/content-elements';

export type QuestionSetElementType = 'mcq' | 'match' | 'fitb-dropdown' | 'fitb-type' | 'ordering';

export type QuestionSetItem =
    | { id: string; type: 'mcq'; data: MCQElementData; points?: number }
    | { id: string; type: 'match'; data: MatchElementData; points?: number }
    | { id: string; type: 'fitb-dropdown'; data: FITBElementData; points?: number }
    | { id: string; type: 'fitb-type'; data: FITBElementData; points?: number }
    | { id: string; type: 'ordering'; data: OrderingElementData; points?: number };

export interface QuestionSetData extends CompositeQuestionsLayoutFields {
    title?: string;
    instructions?: string;
    /** Intro / lesson body — sections and blocks (Page element) */
    pageContent: PageElementData;
    items: QuestionSetItem[];
    /** Remote passage markdown URL when import reuse leaves pageContent empty */
    passageUrl?: string;
    /** Rubric heading from GenResultNode set content.section */
    section?: string;
}

export type QuestionSetSplitRatio = '50-50' | '60-40' | '40-60' | '70-30' | '30-70';

export interface QuestionSetLayoutConfig extends LayoutConfig {
    /** When type is horizontal: lesson column vs questions column */
    splitRatio?: QuestionSetSplitRatio;
    /** Scroll all at once (`all`) or multi-page with tab navigation (`tabs`). Legacy `step` is treated as `tabs` in players. */
    displayMode?: 'all' | 'step' | 'tabs';
    /** MCQ choices: standard = vertical stack (default); adaptive = horizontal grid on lg when there are fewer than five options */
    answerChoicesPresentation?: 'standard' | 'adaptive';
}

export interface QuestionSetContent {
    id: string;
    type: 'question-set';
    version: string;
    data: QuestionSetData;
    config?: QuestionSetConfig;
}

export interface QuestionSetScoringConfig extends ScoringConfig {
    pointsPerQuestion?: number;
    passingThreshold?: number;
}

export interface QuestionSetConfig extends Omit<ContentConfig, 'layout' | 'scoring'> {
    layout?: QuestionSetLayoutConfig;
    scoring?: QuestionSetScoringConfig;
}

export const defaultQuestionSetConfig: QuestionSetConfig = {
    layout: {
        type: 'horizontal',
        splitRatio: '50-50',
        displayMode: 'all',
    },
    behavior: {
        allowRetry: true,
        shuffleChoices: false,
        showCorrectAnswer: true,
        showExplanation: true,
        examMode: false,
        instantFeedback: false,
    },
    scoring: {
        pointsPerQuestion: 1,
        partialCreditEnabled: true,
        passingThreshold: 0.7,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: {
            en: {
                multipleChoice: 'Multiple Choice',
                questionTypeMatch: 'Matching',
                questionTypeOrdering: 'Ordering',
                blankModeDropdown: 'Fill in the Blanks (Dropdown)',
                fillInTheBlanks: 'Fill in the Blanks (Typing)',
                contentTab: 'Content',
                questionsTab: 'Questions',
                configurationTab: 'Configuration',
                title: 'Title',
                enterTitle: 'Enter title',
                instructionsOptional: 'Instructions (Optional)',
                enterInstructions: 'Enter instructions...',
                questions: 'Questions',
                noQuestionsYet: 'No questions yet. Add a question.',
                addSection: 'Add section',
                removeSection: 'Remove section',
                question: 'Question',
                addQuestion: 'Add question',
                selectOrAddQuestion: 'Select a question, a section, or add one to start.',
                removeQuestion: 'Remove question',
                noQuestionsAvailable: 'No questions available',
                passed: 'Passed!',
                failed: 'Keep practicing',
                score: 'Score',
                submit: 'Submit',
                retry: 'Try Again',
                progress: 'Progress',
                answered: 'answered',
                questionNavigation: 'Question navigation',
                previousPage: 'Previous page',
                nextPage: 'Next page',
                noQuestions: 'No questions available',
            },
            vi: {
                multipleChoice: 'Trắc nghiệm',
                questionTypeMatch: 'Nối đáp án',
                questionTypeOrdering: 'Sắp xếp',
                blankModeDropdown: 'Điền khuyết (Chọn)',
                fillInTheBlanks: 'Điền khuyết (Nhập)',
                contentTab: 'Nội dung',
                questionsTab: 'Câu hỏi',
                configurationTab: 'Cấu hình',
                title: 'Tiêu đề',
                enterTitle: 'Nhập tiêu đề',
                instructionsOptional: 'Hướng dẫn (Tuỳ chọn)',
                enterInstructions: 'Nhập hướng dẫn...',
                questions: 'Câu hỏi',
                noQuestionsYet: 'Chưa có câu hỏi. Thêm câu hỏi mới.',
                addSection: 'Thêm nhóm',
                removeSection: 'Xóa nhóm',
                question: 'Câu hỏi',
                addQuestion: 'Thêm câu hỏi',
                selectOrAddQuestion: 'Chọn một câu, một nhóm, hoặc thêm mới.',
                removeQuestion: 'Xóa câu hỏi',
                noQuestionsAvailable: 'Không có câu hỏi nào',
                passed: 'Đã vượt qua!',
                failed: 'Tiếp tục luyện tập',
                score: 'Điểm',
                submit: 'Gửi câu trả lời',
                retry: 'Thử lại',
                progress: 'Tiến độ',
                answered: 'đã trả lời',
                questionNavigation: 'Điều hướng câu hỏi',
                previousPage: 'Trang trước',
                nextPage: 'Trang sau',
                noQuestions: 'Không có câu hỏi nào',
            },
        },
    },
};

export const defaultQuestionSetData: QuestionSetData = {
    // EAA-2082: a new Question Set starts with an EMPTY title so the editor shows only the
    // placeholder. A non-empty default ("Question Set") was picked up by the question-bank page
    // as the content title and saved although nobody typed it.
    title: '',
    instructions: '',
    pageContent: { ...defaultPageData },
    items: [],
};

export const questionSetSchema = {
    type: 'question-set' as const,
    title: 'Question Set',
    description: 'Multiple question types: MCQ, matching, and fill-in-the-blanks',
    version: '1.0.0',
    category: 'assessment' as const,
    icon: 'ListChecks',
    tags: ['question-set', 'assessment', 'mcq', 'match', 'fitb', 'ordering'],
    defaultData: defaultQuestionSetData,
    defaultConfig: defaultQuestionSetConfig,
    fields: [],
};
